import { Check, MessageSquare } from 'lucide-react';
import SchedulingAssignAction from './actions/SchedulingAssignAction';

// 'healthy' is a client-only severity (no backend assembler ever emits it) - used for a
// synthetic "everything's fine" item AIMPage builds for Finance's empty state, reusing
// this exact card shape rather than duplicating its markup. Genuinely a different
// severity than 'info' (which Reports uses for "no module set up yet"), not a repaint.
const SEVERITY_COLORS = { info: '#3b82f6', attention: '#f59e0b', urgent: '#ef4444', healthy: '#22c55e' };

// Registry of domain-specific actions, keyed by an item's sourceType - the template for
// making a domain's items genuinely actionable, not just a link out to chat. Each entry
// is a self-contained component that reads item.data (see aimItemShape.js) and knows how
// to act (see actions/SchedulingAssignAction.js for the reference implementation).
const ACTION_REGISTRY = {
  staffing_gap: SchedulingAssignAction,
};

// Renders any ActionableItem from any AIM domain assembler - the shared header (title,
// severity, description, Ask/Acknowledge/Resolve) is the same regardless of domain (see
// backend/services/aimItemShape.js); a domain-specific action renders below it via the
// registry above when one exists for this item's sourceType.
export default function ActionableItemCard({ item, onOpenChat, onAcknowledge, onResolve, onAfterAction, busy }) {
  const color = SEVERITY_COLORS[item.severity] || SEVERITY_COLORS.info;
  const isEvent = item.kind === 'event';
  const ActionComponent = ACTION_REGISTRY[item.sourceType];

  return (
    <div style={{ ...styles.card, borderLeft: `3px solid ${color}` }}>
      <div style={styles.header}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.titleRow}>
            <span style={styles.title}>{item.title}</span>
            <span style={{ ...styles.badge, background: color + '33', color }}>{item.severity}</span>
            {isEvent && item.status && (
              <span style={styles.statusBadge}>{item.status}</span>
            )}
          </div>
          {item.description && <div style={styles.description}>{item.description}</div>}
        </div>

        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {item.suggestedAction && onOpenChat && (
            <button
              onClick={() => onOpenChat(item.suggestedAction)}
              style={styles.actionBtn}
              title="Ask Aetheris"
            >
              <MessageSquare size={14} /> Ask
            </button>
          )}
          {isEvent && item.status === 'new' && onAcknowledge && (
            <button onClick={() => onAcknowledge(item.entityRef?.id)} disabled={busy} style={styles.neutralBtn}>
              Acknowledge
            </button>
          )}
          {isEvent && item.status !== 'resolved' && onResolve && (
            <button onClick={() => onResolve(item.entityRef?.id)} disabled={busy} style={styles.goodBtn}>
              <Check size={14} /> Resolve
            </button>
          )}
        </div>
      </div>

      {ActionComponent && (
        <div style={styles.actionRow}>
          <ActionComponent item={item} onDone={onAfterAction} />
        </div>
      )}
    </div>
  );
}

const styles = {
  card: { display: 'flex', flexDirection: 'column', padding: '14px 18px', background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(59,130,246,0.15)', borderRadius: 10 },
  header: { display: 'flex', alignItems: 'flex-start', gap: 16 },
  actionRow: { marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(59,130,246,0.1)' },
  titleRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  title: { color: '#e2e8f0', fontWeight: 600, fontSize: 14 },
  badge: { padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, textTransform: 'capitalize' },
  statusBadge: { padding: '2px 8px', borderRadius: 12, fontSize: 11, background: 'rgba(100,116,139,0.2)', color: '#94a3b8', textTransform: 'capitalize' },
  description: { color: '#94a3b8', fontSize: 13, marginTop: 4 },
  actionBtn: { display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: 'rgba(108,99,255,0.15)', border: '1px solid rgba(108,99,255,0.3)', borderRadius: 6, color: '#6C63FF', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' },
  neutralBtn: { padding: '6px 12px', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, color: '#3b82f6', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' },
  goodBtn: { display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 6, color: '#22c55e', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' },
};
